// useAuth.tsx — sin dependencia de Supabase (Fase 4)
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import {
    User,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    updateProfile
} from 'firebase/auth';
import { auth } from '../config/firebase';

export interface UserData {
    id: string;
    tenant_id: string;
    email: string;
    name: string;
    phone: string | null;
    role: 'super_admin' | 'admin' | 'agent' | 'client' | 'courier' | 'operator' | 'branch_agent' | 'supervisor';
    status: string;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Verifica si el usuario logueado es un cliente del portal (no staff)
// llamando a /api/client/profile con su token de Firebase
async function checkIsClient(token: string): Promise<boolean> {
    try {
        const subdomain = import.meta.env.MODE === 'development'
            ? 'demo'
            : (window.location.hostname.split('.')[0] || 'demo');
        const resp = await fetch(`${API_BASE}/api/client/profile`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'X-Tenant-Subdomain': subdomain,
            }
        });
        return resp.ok;
    } catch {
        return false;
    }
}

interface AuthContextType {
    user: User | null;
    userData: UserData | null;
    loading: boolean;
    error: string | null;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, displayName?: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    clearError: () => void;
    fetchUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const EMAIL_ROLE_OVERRIDE: Record<string, UserData['role']> = {
    'jeczito@gmail.com': 'super_admin',
    'admin@demo.com':    'admin',
    'agent@demo.com':    'agent',
    'client@demo.com':  'client',
    'courier@demo.com': 'courier',
};

// Respaldo si /api/users/me no responde (p. ej. el servidor está despertando):
// usar el rol y la empresa guardados en la sesión de Firebase (custom claims que
// pone el servidor al crear el usuario). Antes se asumía 'admin' de ITOBOX para
// cualquiera; sin datos, ahora se usa el rol de menor permiso.
type ClaimsUser = { getIdTokenResult: () => Promise<{ claims: Record<string, any> }> };
const VALID_ROLES: UserData['role'][] = ['super_admin', 'admin', 'agent', 'client', 'courier', 'operator', 'branch_agent', 'supervisor'];

async function roleFromClaims(user: ClaimsUser): Promise<UserData['role']> {
    try {
        const { claims } = await user.getIdTokenResult();
        return VALID_ROLES.includes(claims.role) ? claims.role : 'client';
    } catch {
        return 'client';
    }
}

async function tenantFromClaims(user: ClaimsUser): Promise<string> {
    try {
        const { claims } = await user.getIdTokenResult();
        return typeof claims.tenantId === 'string' ? claims.tenantId : '';
    } catch {
        return '';
    }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser]         = useState<User | null>(null);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState<string | null>(null);

    const fetchUserData = async (): Promise<void> => {
        try {
            if (!user) { setUserData(null); return; }
            const token = await user.getIdToken();
            const response = await fetch(`${API_BASE}/api/users/me`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!response.ok) throw new Error('Error al obtener datos del usuario');
            const data: UserData = await response.json();
            setUserData(data);
        } catch (err: any) {
            console.error('Error obteniendo datos del usuario:', err);
            setUserData(null);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            // Mientras se averigua el rol, las pantallas protegidas muestran "cargando".
            // Antes, justo después de iniciar sesión había usuario pero aún no rol: la
            // pantalla de inicio mandaba de vuelta al login y la página parpadeaba.
            if (firebaseUser) setLoading(true);
            setUser(firebaseUser);

            if (firebaseUser) {
                try {
                    const token = await firebaseUser.getIdToken();
                    const apiUrl = `${API_BASE}/api/users/me`;
                    const response = await fetch(apiUrl, {
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                    });

                    // Helper: el rol viene del API. Solo aplicamos override para cuentas especiales.
                    const applyRoleOverride = (email: string, apiRole: UserData['role']): UserData['role'] => {
                        const lower = email.toLowerCase();
                        return EMAIL_ROLE_OVERRIDE[lower] ?? apiRole;
                    };

                    let resolvedRole: UserData['role'] = 'admin';

                    if (response.ok) {
                        const data: UserData = await response.json();
                        // Usar el rol del API, pero respetar override para cuentas especiales (ej: super_admin)
                        data.role = applyRoleOverride(data.email || firebaseUser.email || '', data.role);
                        console.log('✅ Datos del usuario obtenidos:', data);
                        resolvedRole = data.role;
                        setUserData(data);
                    } else {
                        console.warn('⚠️ /api/users/me no disponible, usando datos de Firebase');
                        const nameFromFirebase = firebaseUser.displayName
                            || firebaseUser.email?.split('@')[0]?.replace(/[._]/g, ' ')
                            || 'Usuario';
                        // Fallback: usar override o 'admin' como default
                        const emailKey = (firebaseUser.email || '').toLowerCase();
                        const fallbackRole: UserData['role'] = EMAIL_ROLE_OVERRIDE[emailKey] ?? await roleFromClaims(firebaseUser);
                        resolvedRole = fallbackRole;
                        setUserData({
                            id: firebaseUser.uid,
                            tenant_id: await tenantFromClaims(firebaseUser),
                            email: firebaseUser.email || '',
                            name: nameFromFirebase.charAt(0).toUpperCase() + nameFromFirebase.slice(1),
                            phone: null,
                            role: fallbackRole,
                            status: 'active'
                        });
                    }

                    // La pantalla de cada rol la abren el login y RoleBasedRedirect. Antes aquí se
                    // recargaba toda la página (window.location) y eso también hacía parpadear.
                    console.log('✅ Rol:', resolvedRole);
                } catch (err) {
                    console.warn('⚠️ Error conectando al backend, usando datos de Firebase:', err);
                    const nameFromFirebase = firebaseUser.displayName
                        || firebaseUser.email?.split('@')[0]?.replace(/[._]/g, ' ')
                        || 'Usuario';
                    const emailKey = (firebaseUser.email || '').toLowerCase();
                    const fallbackRole: UserData['role'] = EMAIL_ROLE_OVERRIDE[emailKey] ?? await roleFromClaims(firebaseUser);
                    setUserData({
                        id: firebaseUser.uid,
                        tenant_id: await tenantFromClaims(firebaseUser),
                        email: firebaseUser.email || '',
                        name: nameFromFirebase.charAt(0).toUpperCase() + nameFromFirebase.slice(1),
                        phone: null,
                        role: fallbackRole,
                        status: 'active'
                    });
                }
            } else {
                setUserData(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const login = async (email: string, password: string): Promise<void> => {
        try {
            setError(null); setLoading(true);
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err: any) {
            const msg = getFirebaseErrorMessage(err.code);
            setError(msg); throw new Error(msg);
        } finally { setLoading(false); }
    };

    const register = async (email: string, password: string, displayName?: string): Promise<void> => {
        try {
            setError(null); setLoading(true);
            const { user } = await createUserWithEmailAndPassword(auth, email, password);
            if (displayName) await updateProfile(user, { displayName });
        } catch (err: any) {
            const msg = getFirebaseErrorMessage(err.code);
            setError(msg); throw new Error(msg);
        } finally { setLoading(false); }
    };

    const logout = async (): Promise<void> => {
        try { setError(null); await signOut(auth); }
        catch (err: any) { setError('Error al cerrar sesión'); throw err; }
    };

    const resetPassword = async (email: string): Promise<void> => {
        try { setError(null); await sendPasswordResetEmail(auth, email); }
        catch (err: any) { const msg = getFirebaseErrorMessage(err.code); setError(msg); throw new Error(msg); }
    };

    return (
        <AuthContext.Provider value={{ user, userData, loading, error, login, register, logout, resetPassword, clearError: () => setError(null), fetchUserData }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    return context;
};

function getFirebaseErrorMessage(code: string): string {
    const msgs: Record<string, string> = {
        'auth/invalid-email':          'El correo electrónico no es válido',
        'auth/user-disabled':          'Esta cuenta ha sido deshabilitada',
        'auth/user-not-found':         'No existe una cuenta con este correo',
        'auth/wrong-password':         'Contraseña incorrecta',
        'auth/invalid-credential':     'Credenciales inválidas',
        'auth/email-already-in-use':   'Este correo ya está registrado',
        'auth/weak-password':          'La contraseña debe tener al menos 6 caracteres',
        'auth/too-many-requests':      'Demasiados intentos. Intenta más tarde',
        'auth/network-request-failed': 'Error de conexión. Verifica tu internet',
    };
    return msgs[code] || 'Error de autenticación';
}

export default useAuth;
